/**
 * Response of POST turbine/restore.
 *
 * The backend no longer queues a restore when the data is already readable or a restore is already
 * running (NAMC-8445); in that case it answers with a no-op status and no ETA. Showing the
 * "Restore Request Submitted / you will receive an email" modal for those answers would promise the
 * user something that is never going to happen.
 *
 * This app renders its own local restore modals rather than the shared ones, so the helper exposes
 * the decision instead of rendering it.
 */
export interface RestoreResponse {
  status?: string;
  message?: string;
  etaGuaranteedUtc?: string | null;
}

export const RESTORE_STATUS = {
  accepted: 'accepted',
  alreadyRestored: 'already_restored',
  alreadyInProgress: 'already_in_progress'
} as const;

/**
 * True when a restore was actually queued. Anything unexpected — an older backend, an unreadable
 * body — counts as queued, which is the pre-NAMC-8445 behaviour.
 */
export const isRestoreQueued = (data?: RestoreResponse | null): boolean =>
  data?.status !== RESTORE_STATUS.alreadyRestored && data?.status !== RESTORE_STATUS.alreadyInProgress;

export const describeRestoreNoOp = (data?: RestoreResponse | null): string =>
  data?.status === RESTORE_STATUS.alreadyInProgress
    ? 'A restore is already running for this turbine. You will receive an email once the data is available.'
    : 'The turbine data is already available. No restore was needed.';
